import {
  HomeWorkoutConcurrentCachingAlgo,
  HomeWorkoutSequence,
  IAudioTip,
  SessionSequenceEntry,
} from 'fitbeat.gym-core';
import { Logger } from 'fitbeat.react-native-components';
import { IWorkoutAssetHelper, IFileInfo, IFileSystem } from 'fitbeat.react-native-components/HomeWorkouts';
import { Platform } from 'react-native';
import FastImage from 'react-native-fast-image';
import { AudioHelper } from './AudioHelper';
import { getConfig } from '../../config';

const WORKOUTS_FOLDER = 'fitbeat-workouts';

export interface IWorkoutCacheEntry {
  index: number;
  name: string;
  cachingProcessIdentifier: number;
  startCache(): Promise<void>;
}
enum FileType {
  MP3 = 'mp3',
  MP4 = 'mp4',
}
export class WorkoutAssetHelper implements IWorkoutAssetHelper {
  public static stopCurrentRunningCachingProcess() {
    HomeWorkoutConcurrentCachingAlgo.requestProcessingToStop();
  }

  private newConcurrentCachingAlgo: HomeWorkoutConcurrentCachingAlgo | undefined;

  constructor(
    private fileSystem: IFileSystem,
    private downloadProgressCallback?: (progress: number) => void,
  ) { }

  public async getVideoFileUrl(
    workoutId: string,
    exerciseName: string,
    exerciseVersion: string,
  ): Promise<string> {
    const filePath = this.buildFileCachePath(workoutId, exerciseName, FileType.MP4);
    const fileInfoInCache = await this.getFileInfo(filePath);
    if (!fileInfoInCache.exists) {
      throw new Error(`${filePath} does not exist in cache`);
    }

    Logger.leaveBreadcrumb('Video file info from cache', fileInfoInCache);
    return filePath;
  }

  public async getAudioFileUrl(
    workoutSession: HomeWorkoutSequence,
    audioFileName: string,
    exerciseVersion: string,
  ): Promise<string> {
    const filePath = this.buildFileCachePath(workoutSession.workoutId, audioFileName, FileType.MP3);
    const fileInfoInCache = await this.getFileInfo(filePath);
    if (fileInfoInCache.exists) {
      Logger.leaveBreadcrumb('Audio file info from cache', fileInfoInCache);
      return filePath;
    }

    return this.buildAssetUrl(
      workoutSession,
      'tips',
      audioFileName,
      FileType.MP3,
      `version=${exerciseVersion || ''}`,
    );
  }

  public async cacheWorkoutAssets(
    workoutSession: HomeWorkoutSequence,
    currentIndex: number,
    numberOfEntriesThatMustSucceed: number,
    sequenceCachedSuccessfully: (indexes: number[]) => void,
    sequenceCachedFailed: (index: number) => void,
    downloadProgressUpdated: (progress: number) => void,
  ) {
    try {
      await this.cleanOldCacheFolders(workoutSession.workoutId);
      await this.ensureWorkoutCacheDirectoryExists(workoutSession.workoutId);
      await this.logFreeDiskSpace();
      const setupParams = HomeWorkoutConcurrentCachingAlgo.setupDefaultParams(
        currentIndex,
        numberOfEntriesThatMustSucceed,
      );
      const cacheEntriesLeftToProcess = this.populateCacheEntries(
        workoutSession,
        setupParams.cachingProcessIdentifier,
        currentIndex,
      );
      this.downloadProgressCallback = downloadProgressUpdated;
      this.newConcurrentCachingAlgo = new HomeWorkoutConcurrentCachingAlgo(
        setupParams,
        cacheEntriesLeftToProcess,
        Logger,
        sequenceCachedSuccessfully,
        sequenceCachedFailed,
      );
      await this.newConcurrentCachingAlgo.processAllConcurrently();
    } catch (error) {
      Logger.notify(error);
      sequenceCachedFailed(1);
    }
  }

  private populateCacheEntries(
    workoutSession: HomeWorkoutSequence,
    cachingProcessIdentifier: number,
    currentIndex: number,
  ): IWorkoutCacheEntry[] {
    const cacheEntries: IWorkoutCacheEntry[] = [];
    workoutSession.sequences.map((sequenceEntry: SessionSequenceEntry, index: number) => {
      const audioTipEntriesBeforeFiltered = sequenceEntry.audioTips || [];
      const audioTipEntries = audioTipEntriesBeforeFiltered.filter((tip) => !tip.shouldNotPreload);
      const audioFileNames = audioTipEntries.map((audioTip: IAudioTip) => (audioTip.fileName ? audioTip.fileName : ''));
      const imageUrls = new Array<string>();
      if (sequenceEntry.homeBenchInstructionImageUrl) {
        imageUrls.push(sequenceEntry.homeBenchInstructionImageUrl);
      }
      const newCacheEntry = {
        index,
        name: sequenceEntry.videoFile,
        cachingProcessIdentifier,
      } as IWorkoutCacheEntry;
      // added this command so that this object can cache itself without algo knowing about details
      newCacheEntry.startCache = () => this.cacheSingle(
        sequenceEntry.videoFile,
        sequenceEntry.exerciseVersion,
        audioFileNames,
        imageUrls,
        workoutSession,
      );
      if (index >= currentIndex) {
        cacheEntries.push(newCacheEntry);
      }
    });
    return cacheEntries;
  }

  // download and cache video and corresponding audio a workout sequence entry
  private async cacheSingle(
    videoFileName: string,
    exerciseVersion: string,
    audioFileNames: string[],
    imageUrls: string[],
    workoutSession: HomeWorkoutSequence,
  ): Promise<void> {
    const disableRetryOnVideoLevel = true;
    await this.cacheFileIfNotExists(
      workoutSession.workoutId,
      videoFileName,
      FileType.MP4,
      async () => this.buildAssetUrl(
        workoutSession,
        'videos',
        videoFileName,
        FileType.MP4,
        `version=${exerciseVersion}`,
      ),
      disableRetryOnVideoLevel,
    );
    for (const fileName of audioFileNames) {
      await this.cacheFileIfNotExists(
        workoutSession.workoutId,
        fileName,
        FileType.MP3,
        async () => this.buildAssetUrl(
          workoutSession,
          'tips',
          fileName,
          FileType.MP3,
          `version=${exerciseVersion}`,
        ),
      );
    }

    this.cacheImages(imageUrls);
  }

  private cacheImages(imageUrls) {
    if (imageUrls && imageUrls.length > 0) {
      Logger.leaveBreadcrumb('Preloading images ', { imageUrls });
    }
    for (const imageUrl of imageUrls) {
      FastImage.preload([{ uri: imageUrl }]);
    }
  }

  private async cacheFileIfNotExists(
    workoutId: string,
    exerciseName: string,
    extension: FileType,
    urlResolver: () => Promise<string>,
    disableRetryOnce = false,
  ): Promise<void> {
    const tryCache = async () => {
      const start = Date.now();
      const fileInfoFromCache = await this.getFileInfo(
        this.buildFileCachePath(workoutId, exerciseName, extension),
      );

      if (fileInfoFromCache.exists) {
        Logger.debug(`Video for ${exerciseName} in cache, not downloading again`);
        return;
      }

      await this.ensureWorkoutCacheDirectoryExists(workoutId);

      const videoDownloadPath = this.buildFileCachePath(workoutId, exerciseName, extension);
      const downloadUrl = await urlResolver();
      const downloadProgressForBigFiles = (
        downloadProgress: { totalBytesWritten: number; totalBytesExpectedToWrite: number },
      ) => {
        const minimumFileSizeToReportOnInBytes = 1024 * 1024;
        if (
          downloadProgress.totalBytesExpectedToWrite >= minimumFileSizeToReportOnInBytes
          && this.downloadProgressCallback
        ) {
          const progress = downloadProgress.totalBytesWritten / downloadProgress.totalBytesExpectedToWrite;
          this.downloadProgressCallback(progress);
        }
      };
      const result = await this.fileSystem.downloadAsync(
        downloadUrl,
        videoDownloadPath,
        downloadProgressForBigFiles,
      );
      if (result.status !== 200) {
        throw new Error(`failed to download ${exerciseName}`);
      }

      const downloadedFileInfo = await this.fileSystem.getInfoAsync(result.uri);
      if (!downloadedFileInfo.exists) {
        throw new Error(`video does not exist after download ${result.uri}`);
      }

      const end = Date.now();
      Logger.debug(`Video downloaded and cached for ${exerciseName} took ${end - start} milliseconds`);
    };

    try {
      await tryCache();
    } catch (e) {
      await this.logFreeDiskSpace();
      if (disableRetryOnce) {
        throw e;
      } else {
        Logger.leaveBreadcrumb(`Failed to cache ${exerciseName} with: ${e.message}, trying one more time...`);
        await tryCache();
      }
    }
  }

  private async getFileInfo(filePath: string): Promise<IFileInfo> {
    if (!this.fileSystem.cacheDirectory) {
      return {
        exists: false,
      };
    }

    const fileInfo = await this.fileSystem.getInfoAsync(filePath);
    return fileInfo;
  }

  private buildFileCachePath(workoutId: string, exerciseName: string, extension: FileType | string): string {
    return `${this.buildRootCachePath()}/${workoutId}/${exerciseName}.${extension}`;
  }

  private buildWorkoutPath(workoutId: string) {
    return `${this.buildRootCachePath()}/${workoutId}`;
  }

  private buildRootCachePath(): string {
    return `${this.fileSystem.cacheDirectory}${WORKOUTS_FOLDER}`;
  }

  private async ensureWorkoutCacheDirectoryExists(workoutId: string) {
    const rootFolderInfo = await this.fileSystem.getInfoAsync(this.buildRootCachePath());
    if (!rootFolderInfo.exists) {
      Logger.leaveBreadcrumb('creating root folder for caching');
      await this.fileSystem.makeDirectoryAsync(this.buildRootCachePath());
    }

    const workoutFolderPath = this.buildWorkoutPath(workoutId);
    const workoutFolderInfo = await this.fileSystem.getInfoAsync(workoutFolderPath);
    if (workoutFolderInfo.exists) {
      Logger.debug(`cache directory ${workoutFolderPath} already exists`);
    } else {
      await this.fileSystem.makeDirectoryAsync(workoutFolderPath);
      Logger.leaveBreadcrumb(`cache directory ${workoutFolderPath} created`);
    }
  }

  private async cleanOldCacheFolders(currentWorkoutId: string) {
    const getRootCacheDirectoryFiles = async () => {
      try {
        const folders = await this.fileSystem.readDirectoryAsync(this.buildRootCachePath());
        return folders;
      } catch (e) {
        Logger.leaveBreadcrumb(`failed to read root cache directory when cleaning old cache folders ${e}`);
        return [];
      }
    };

    try {
      const allWorkoutFolders = await getRootCacheDirectoryFiles();
      const oldWorkoutCachePaths = allWorkoutFolders.filter((path: string) => path.indexOf(currentWorkoutId) === -1);
      Logger.leaveBreadcrumb(`clearing ${oldWorkoutCachePaths.length} old workout directories`);
      for (const oldWorkoutCachePath of oldWorkoutCachePaths) {
        await this.fileSystem.deleteAsync(
          this.buildWorkoutPath(oldWorkoutCachePath),
        );
      }
    } catch (e) {
      Logger.notify(e);
    }
  }

  private async logFreeDiskSpace() {
    try {
      const mb = await this.fileSystem.getFreeDiskStorageMegaBytes();
      Logger.leaveBreadcrumb(`${mb}mb free storage space`);
    } catch (e) {
      Logger.leaveBreadcrumb('failed to check free storage space', e);
    }
  }

  private buildAssetUrl(
    session: HomeWorkoutSequence,
    subPath: string,
    fileName: string,
    extension: FileType,
    query: string,
  ) {
    const {
      baseUrl,
      queryParamatersToAppend,
    } = session.mediaCdnConfiguration;
    const url = new URL(
      `${subPath}/${fileName}.${extension}`,
      baseUrl,
    );
    const fullPath = `${url.href}?${query}&${queryParamatersToAppend}`;
    return fullPath;
  }
}
